import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./upgradePlan.scss";

import { StoragePlan, ManageUser } from "@induz/sdk";
import Button from "components/Button/index";
import emailStar from "assets/images/organisationProfile/pricing/emailStar.svg";
import { useToast } from "components/Toaster/index";
import { signInContext } from "context/SigninContext";
import { signupContext } from "context/SignupContext";

const FeatureItem = ({ icon, text }) => (
  <div className="folder">
    <img src={icon} alt="feature icon" />
    <p>{text}</p>
  </div>
);

const normalizeName = (name = "") => name.toLowerCase().trim();

// Individual plans config - same as signup pricing page
const INDIVIDUAL_PLANS_CONFIG = [
  {
    name: "Free",
    price: 0,
    badge: null,
    storage: {
      users: "3",
      user: "8 GB",
      team: "5 spaces"
    },
    stats: [
      { label: "Redundancy", value: "LRS" },
      { label: "Support", value: "Regular" }
    ],
    features: [
      "Ransomware protection",
      "Collaboration, internal sharing, docs & sheets",
      "Parallel edits: 5 * • Versions: 10",
      "Ledger logs"
    ],
    buttonText: "Get Started",
    matcher: "Free",
    order: 0
  },
  {
    name: "Basic",
    price: 150,
    badge: null,
    storage: {
      users: "5",
      user: "100 GB",
      team: "25 spaces"
    },
    stats: [
      { label: "Redundancy", value: "LRS" },
      { label: "Support", value: "Regular" }
    ],
    features: [
      "Ransomware protection",
      "Collaboration, internal sharing, docs & sheets",
      "Parallel edits: 5 * • Versions: 10"
    ],
    buttonText: "Get Started",
    matcher: "Basic",
    order: 1
  },
  {
    name: "Pro",
    price: 700,
    badge: null,
    storage: {
      users: "10",
      user: "500 GB",
      team: "50 spaces"
    },
    stats: [
      { label: "Redundancy", value: "LRS" },
      { label: "Support", value: "Advanced S" }
    ],
    features: [
      "Ransomware protection",
      "Collaboration, internal sharing, docs & sheets",
      "Parallel edits: 20 * • Versions: 10",
      "Ysentinel: 100/day • Configurable alerts: 50",
      "Log period: 3 months"
    ],
    buttonText: "Get Started",
    matcher: "Pro",
    order: 2
  },
  {
    name: "Ultra Pro",
    price: 1500,
    badge: null,
    storage: {
      users: "10",
      user: "1 TB",
      team: "100 spaces"
    },
    stats: [
      { label: "Redundancy", value: "ZRS" },
      { label: "Support", value: "One day support" }
    ],
    features: [
      "Ransomware protection",
      "Collaboration, internal sharing, docs & sheets",
      "Parallel edits: 100 * • Versions: 100",
      "Ysentinel: 500/day • Configurable alerts: 100",
      "Log period: 6 months"
    ],
    buttonText: "Get Started",
    matcher: "Ultra Pro",
    order: 3
  }
];

const formatPrice = (price) =>
  typeof price === "number"
    ? `₹${new Intl.NumberFormat("en-IN").format(price)}`
    : price;

function UpgradePlan() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlanIndex, setCurrentPlanIndex] = useState(0);
  const [activePlan, setActivePlan] = useState(null);
  const [upgradeInProgress, setUpgradeInProgress] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const showToast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(signInContext);
  const { setSelectedPlanData, setIsTypeSelect, setUser: setSignupUser, setOrgnization: setSignupOrg } = useContext(signupContext);
  const renewalOnly = Boolean(location.state?.renewalOnly);
  const planTier = sessionStorage.getItem("plan")?.toLowerCase?.();
  const isFreePlan = planTier === "free";

  // Organization employees cannot upgrade plan; redirect to homepage
  const userType = sessionStorage.getItem("usertype");
  const isEmployeeUser = userType === "Employee";
  useEffect(() => {
    if (isEmployeeUser) {
      navigate("/homepage", { replace: true });
    }
  }, [isEmployeeUser, navigate]);

  // Check if plan is a "Contact us" or "Talk to us" plan
  const isContactUsPlan = (planName) => {
    const normalized = normalizeName(planName);
    return normalized.includes("contact") || normalized.includes("talk to us") || normalized.includes("talktous");
  };

  // Calculate price based on billing period (annual is 10x monthly for individual, 11x for org)
  const getPlanPrice = (basePrice) => {
    if (typeof basePrice !== "number" || basePrice === 0) return basePrice;

    // Check if user is organization user (SuperAdmin or Employee)
    const userType = sessionStorage.getItem("usertype");
    const isOrgUser = userType === "SuperAdmin" || userType === "Employee";

    // Use 11x multiplier for organization users, 10x for individual users
    const multiplier = isOrgUser ? 11 : 10;
    return billingPeriod === "monthly" ? basePrice : basePrice * multiplier;
  };

  useEffect(() => {
    if (renewalOnly && (isEmployeeUser || isFreePlan)) {
      navigate("/homepage", { replace: true });
    }
  }, [renewalOnly, isEmployeeUser, isFreePlan, navigate]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        console.log("🔍 Fetching upgrade plans from backend...");
        setLoading(true);
        const storagePlan = new StoragePlan();

        // Determine user type (individual or organization)
        const userType = sessionStorage.getItem("usertype");
        const isOrg = userType === "SuperAdmin" || userType === "Employee";
        const customerType = isOrg ? "org" : "ind";

        console.log("🔍 User type:", userType, "Customer type:", customerType);

        let backendPlansList = [];
        try {
          // Try upgradePlan first (returns next tier plans for upgrade)
          const userMail = sessionStorage.getItem("mail") || "";
          const organization = sessionStorage.getItem("organization") || "";
          backendPlansList = await storagePlan.upgradePlan(userMail, organization);
          console.log("✅ Plans from upgradePlan API:", backendPlansList);
        } catch (err) {
          console.warn("⚠️ upgradePlan failed, falling back to listPlans:", err);
          // Fallback to listPlans with customer type (same as Pricing component)
          backendPlansList = await storagePlan.listPlans(customerType);
          console.log("✅ Plans from listPlans API:", backendPlansList);
        }

        // Filter plans by type to ensure we only get plans for the correct customer type
        const filteredPlans = backendPlansList.filter(p => {
          const planType = p.getType?.();
          return planType === customerType;
        });

        console.log("🔍 Filtered plans for customer type:", customerType, "Count:", filteredPlans.length);

        const mapBackendPlanToCard = (bp, index) => {
          // isOrg is available in the closure from the outer scope
          const toNumber = (val) => {
            if (val === null || val === undefined) return undefined;
            if (typeof val === "object" && typeof val.toNumber === "function") return val.toNumber();
            return Number(val);
          };

          const formatStorage = (val) => {
            const num = toNumber(val);
            if (num === undefined || Number.isNaN(num) || num === 0) return "0 B";

            // Convert bytes to human-readable format (GB, TB, PB)
            const k = 1024;
            const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
            const i = Math.floor(Math.log(num) / Math.log(k));

            // Clamp index to valid range
            const sizeIndex = Math.min(i, sizes.length - 1);
            const value = num / Math.pow(k, sizeIndex);

            // Format with appropriate decimal places
            let formattedValue;
            if (sizeIndex >= 3) { // GB, TB, PB - show 2 decimal places
              formattedValue = Math.round(value * 100) / 100;
            } else { // B, KB, MB - show whole numbers
              formattedValue = Math.round(value);
            }

            return `${formattedValue} ${sizes[sizeIndex]}`;
          };

          // Get all plan properties from backend
          const price = toNumber(bp.getPrice?.());
          const storageBytes = toNumber(bp.getStorage?.());
          const maxSpaces = toNumber(bp.getMaxSpaces?.());
          const minUsers = toNumber(bp.getMinUsers?.());
          const maxAlerts = toNumber(bp.getMaxAlerts?.());
          const logsHistoryDays = toNumber(bp.getLogsHistory?.());
          const trashFromPlan = toNumber(bp.getTrash?.());
          const maxTags = toNumber(bp.getTags?.());
          const maxDevices = bp.getMaxDevices ? toNumber(bp.getMaxDevices()) : 0;
          const parallelEdits = bp.getParallelEdits ? toNumber(bp.getParallelEdits()) : 0;
          const maxVersions = bp.getMaxVersions ? toNumber(bp.getMaxVersions()) : null;
          const planName = bp.getname?.() || `Plan ${index + 1}`;
          const planId = bp.getPlanId?.();
          const redundancy = bp.getRedundancy ? bp.getRedundancy() : "LRS";
          const support = bp.getSupport ? bp.getSupport() : "Regular";
          const ransomwareProtection = bp.getRansomwareProtection ? bp.getRansomwareProtection() : false;
          const collaboration = bp.getCollaboration ? bp.getCollaboration() : false;
          const internalSharing = bp.getInternalSharing ? bp.getInternalSharing() : false;
          const tagBasedAnalysis = bp.getTagBasedAnalysis ? bp.getTagBasedAnalysis() : false;
          const complianceReports = bp.getComplianceReports ? bp.getComplianceReports() : false;
          const geoLocationAlerts = bp.getGeoLocationBasedAlerts ? bp.getGeoLocationBasedAlerts() : false;
          const dedicatedCluster = bp.getDedicatedCluster ? bp.getDedicatedCluster() : false;

          // Convert bytes to human-readable format (GB, TB, PB)
          const storageFormatted = formatStorage(storageBytes);

          // Build features array based on backend plan properties (same as Pricing component)
          const features = [];

          if (ransomwareProtection) {
            if (collaboration && internalSharing) {
              features.push("* Ransomware protection + collaboration suite");
            } else {
              features.push("Ransomware protection");
            }
          } else if (collaboration && internalSharing) {
            features.push("* Collaboration, internal sharing, docs & sheets");
          }

          if (parallelEdits > 0) {
            // Use maxVersions from backend if available, otherwise calculate from logsHistory
            const versions = maxVersions !== null && maxVersions !== undefined && maxVersions > 0
              ? maxVersions
              : (logsHistoryDays > 0 ? Math.floor(logsHistoryDays / 30) : 10);
            features.push(`Parallel edits: ${parallelEdits} * • Versions: ${versions}`);
          }

          if (isOrg) {
            // Organization-specific features
            if (maxAlerts > 0) {
              const ysentinelMonths = logsHistoryDays > 0 ? Math.floor(logsHistoryDays / 30) : 3;
              features.push(`Ysentinel period: ${ysentinelMonths} months`);
              features.push(`* Configurable alerts: ${maxAlerts} • Log period: ${Math.floor(logsHistoryDays / 30)} months`);
            }

            if (maxTags > 0) {
              const tagText = tagBasedAnalysis 
                ? `Max tags per file: ${maxTags} • Tag based analysis *`
                : `Max tags per file: ${maxTags}`;
              features.push(tagText);
            }

            if (complianceReports) {
              features.push(`Compliance reports: 10 / month *${geoLocationAlerts ? " • Geo-location alerts *" : ""}`);
            }

            if (dedicatedCluster) {
              features.push("Compliance + geo alerts * • Dedicated cluster");
              features.push("Designed for regulated workloads & throughput");
            }
          } else {
            // Individual-specific features
            if (maxAlerts > 0) {
              const lowerName = planName.toLowerCase();
              let ysentinelPerDay;
              
              if (lowerName.includes("pro")) {
                ysentinelPerDay = lowerName.includes("ultra") ? 500 : 100;
              } else {
                ysentinelPerDay = Math.floor(maxAlerts / (logsHistoryDays || 90));
              }

              features.push(`* Ysentinel: ${ysentinelPerDay}/day • Configurable alerts: ${maxAlerts}`);
            }

            if (logsHistoryDays > 0) {
              features.push(`Log period: ${Math.floor(logsHistoryDays / 30)} months`);
            }

            // Show tags for individual plans if available
            if (maxTags > 0) {
              const tagText = tagBasedAnalysis 
                ? `Max tags per file: ${maxTags} • Tag based analysis *`
                : `Max tags per file: ${maxTags}`;
              features.push(tagText);
            }

            if (index === 0) {
              features.push("Ledger logs");
            }
          }

          features.push(`Trash retention: ${trashFromPlan}`);

          // Determine badge based on index (for org plans)
          let badge = null;
          if (isOrg) {
            if (index === 1) {
              badge = "Popular";
            } else if (index === 2) {
              badge = "Scale";
            } else if (index === 3) {
              badge = "Enterprise";
            }
          }

          // Format storage for display
          const spacesFormatted = maxSpaces > 0 ? `${maxSpaces} spaces` : "Unlimited";
          const usersFormatted = isOrg
            ? (minUsers > 0 ? `Up to ${minUsers} users` : "Unlimited users")
            : (maxDevices > 0 ? maxDevices.toString() : "3");

          // Check if it's a contact plan (usually the highest tier org plan)
          // More flexible check: if price is 0 or plan name contains contact/enterprise/ultra pro
          const normalizedPlanName = planName.toLowerCase();
          const isContact = isOrg && (
            price === 0 ||
            normalizedPlanName.includes("enterprise") ||
            normalizedPlanName.includes("ultra pro") ||
            normalizedPlanName.includes("contact") ||
            normalizedPlanName.includes("talk to us")
          );

          const stats = [
            { label: "Redundancy", value: redundancy || "LRS" },
            { label: "Support", value: support || "Regular" },
            { label: "Trash", value: String(trashFromPlan) }
          ];

          return {
            name: planName,
            price: isContact ? "Contact us" : (price ?? 0),
            badge: badge,
            storage: {
              users: usersFormatted,
              user: storageFormatted,
              team: spacesFormatted
            },
            stats,
            features: features.length > 0 ? features : ["Standard features included"],
            buttonText: isContact ? "Talk to us" : "Upgrade",
            matcher: planName,
            order: index,
            planId: planId,
            trashRetentionDays: trashFromPlan,
            billingPeriod: "monthly",
            isContact: isContact
          };
        };

        const backendPlanCards = filteredPlans.map((bp, idx) =>
          mapBackendPlanToCard(bp, idx)
        );

        // Sort plans by price (ascending) to maintain consistent order
        backendPlanCards.sort((a, b) => {
          const priceA = typeof a.price === "number" ? a.price : (a.price === "Contact us" ? Infinity : 0);
          const priceB = typeof b.price === "number" ? b.price : (b.price === "Contact us" ? Infinity : 0);
          return priceA - priceB;
        });

        const plansToUse =
          backendPlanCards.length > 0 ? backendPlanCards : INDIVIDUAL_PLANS_CONFIG;

        console.log(
          "🔍 DEBUG: Mapped plans:",
          plansToUse.map((p) => ({
            name: p.name,
            planId: p.planId,
            trashRetentionDays: p.trashRetentionDays
          }))
        );

        setPlans(plansToUse);

        // Determine current plan using plan ID (most reliable method)
        await determineCurrentPlan(plansToUse, backendPlansList);
      } catch (error) {
        console.error("Failed to fetch upgrade plans:", error);
        // Fallback to config if fetch fails
        setPlans(INDIVIDUAL_PLANS_CONFIG);
        determineCurrentPlan(INDIVIDUAL_PLANS_CONFIG, []);
      } finally {
        setLoading(false);
      }
    };

    const determineCurrentPlan = async (plansList, backendPlansList) => {
      let currentPlanIndex = 0; // Default to first plan (index 0)

      console.log("🔍 DEBUG: Starting plan detection (backend only, no sessionStorage)...");
      console.log("🔍 DEBUG: Available plans:", plansList.map(p => ({ name: p.name, planId: p.planId })));
      console.log("🔍 DEBUG: User object available:", !!user);

      // Try to get user if not available in context
      let currentUser = user;
      if (!currentUser) {
        try {
          const userMail = sessionStorage.getItem("mail");
          if (userMail) {
            currentUser = await ManageUser.getUserDetails(userMail);
            console.log("🔍 DEBUG: Fetched user from backend:", !!currentUser);
          }
        } catch (error) {
          console.error("❌ Error fetching user:", error);
        }
      }

      // Use ONLY backend planId from user object (source of truth)
      if (currentUser && currentUser.getPlan && typeof currentUser.getPlan === "function") {
        try {
          const userPlanId = currentUser.getPlan();
          console.log("🔍 Plan ID from backend (user.getPlan()):", userPlanId, "type:", typeof userPlanId);

          if (userPlanId !== null && userPlanId !== undefined && userPlanId >= 0) {
            // Find the plan in our mapped plans list using planId
            const currentPlan = plansList.find(
              (p) => p.planId === userPlanId
            );

            console.log("🔍 DEBUG: Looking for planId", userPlanId, "in plans:", plansList.map(p => ({ name: p.name, planId: p.planId })));

            if (currentPlan) {
              currentPlanIndex = plansList.indexOf(currentPlan);
              console.log("✅ SUCCESS: Found current plan by backend planId:", currentPlan.name, "at index:", currentPlanIndex, "planId:", currentPlan.planId);

              // No need to store in sessionStorage - backend is the source of truth
            } else {
              console.warn("⚠️ Plan ID", userPlanId, "not found in plans list. Available planIds:", plansList.map(p => p.planId));
              console.warn("⚠️ Defaulting to first plan (index 0):", plansList[0]?.name);
            }
          } else {
            console.warn("⚠️ Invalid plan ID from backend:", userPlanId);
            console.warn("⚠️ Defaulting to first plan (index 0):", plansList[0]?.name);
          }
        } catch (error) {
          console.error("❌ Error getting plan from user object:", error);
          console.warn("⚠️ Defaulting to first plan (index 0):", plansList[0]?.name);
        }
      } else {
        console.warn("⚠️ User object not available or getPlan() not found");
        console.warn("⚠️ Defaulting to first plan (index 0):", plansList[0]?.name);
      }

      // Ensure index is valid
      if (currentPlanIndex < 0 || currentPlanIndex >= plansList.length) {
        currentPlanIndex = 0;
        console.warn("⚠️ Invalid plan index. Defaulting to first plan (index 0).");
      }

      const finalPlan = plansList[currentPlanIndex];
      console.log("📋 FINAL: Current plan determined:", finalPlan?.name, "at index:", currentPlanIndex, "planId:", finalPlan?.planId);
      console.log("📋 FINAL: Will show plans from index", currentPlanIndex, "onwards:", plansList.slice(currentPlanIndex).map(p => ({ name: p.name, planId: p.planId })));
      setCurrentPlanIndex(currentPlanIndex);
    };

    fetchPlans();
  }, [user]);

  const isPlanFree = (plan) => {
    const priceVal = plan?.price;
    if (priceVal === 0) return true;
    if (typeof priceVal === "string" && normalizeName(priceVal).includes("free")) return true;
    return false;
  };

  // Show only current plan + higher tier plans (NO DOWNGRADES)
  // Example scenarios:
  // - If current plan is Free (index 0) → show [Free, Basic, Pro, Ultra Pro]
  // - If current plan is Basic (index 1) → show [Basic, Pro, Ultra Pro] (NO Free)
  // - If current plan is Pro (index 2) → show [Pro, Ultra Pro] (NO Free, NO Basic)
  // - If current plan is Ultra Pro (index 3) → show [Ultra Pro] only (NO downgrades)
  const visiblePlans = plans.slice(currentPlanIndex);
  const plansToRender = renewalOnly ? visiblePlans.slice(0, 1) : visiblePlans;

  useEffect(() => {
    if (!renewalOnly) return;
    const firstPlan = plansToRender[0];
    if (!firstPlan) return;
    if (isPlanFree(firstPlan)) {
      navigate("/homepage", { replace: true });
    }
  }, [renewalOnly, plansToRender, navigate]);

  const handleUpgradeClick = async (plan, options = {}) => {
    const { isRenewal = false } = options;

    console.log("[UpgradePlan] plan selected", {
      name: plan.name,
      planId: plan.planId,
      trashRetentionDays: plan.trashRetentionDays
    });

    // This should not be called for contact plans (handled in button onClick)
    // But add a safety check just in case
    if (plan.isContact || isContactUsPlan(plan.name)) {
      window.location.href = "mailto:sales@induz.com";
      return;
    }

    // Prevent parallel upgrades
    if (upgradeInProgress) return;
    setUpgradeInProgress(plan.planId ?? "unknown");

    try {
      const actionLabel = isRenewal ? "renewal" : "upgrade";
      if (plan.planId === undefined || plan.planId === null) {
        showToast("Plan ID missing, proceeding to payment placeholder.", "warning");
        console.warn(`${actionLabel} proceeding without planId (placeholder):`, plan);
      } else {
        showToast(`Starting ${actionLabel} for ${plan.name}...`, "info");
      }

      const userMail = sessionStorage.getItem("mail");
      const payload = {
        planId: plan.planId, // may be undefined in placeholder mode
        planName: plan.name,
        userMail,
        // TODO: backend devs — wire this to your payment/upgrade API.
        // Example:
        // const payment = await paymentClient.startPayment(plan.planId, { annual: false });
        // redirectTo(payment.url);
      };

      console.log("🚧 TODO: Call backend upgrade API with payload:", payload);

      // No need to store in sessionStorage - backend will handle plan updates after payment

      // Calculate final price with billing period
      const planData = {
        ...plan,
        price: getPlanPrice(plan.price),
        originalPrice: plan.price,
        billingPeriod: billingPeriod,
        isRenewal: isRenewal
      };

      // Hydrate signup context so the shared payment flow has plan + user info
      setSelectedPlanData(planData);
      const isOrgCustomer = userType === "SuperAdmin" || userType === "Employee";
      setIsTypeSelect(isOrgCustomer ? "org" : "individual");
      if (user) {
        setSignupUser(user);
      }
      // If org upgrades are needed and org is in signInContext, setSignupOrg(orgnization);

      // Redirect to the shared payment flow (Razorpay)
      navigate("/payment", { state: { plan: planData } });

      showToast(
        `${isRenewal ? "Renewal" : "Upgrade"} for ${plan.name} initiated. Complete payment to finish.`,
        "success"
      );
    } catch (error) {
      console.error("Upgrade initiation failed:", error);
      showToast("Failed to start upgrade. Please try again.", "error");
    } finally {
      setUpgradeInProgress(null);
    }
  };

  if (isEmployeeUser) {
    return null;
  }

  return (
    <section className={`UpgradePlanPage ${renewalOnly ? "renewalMode" : ""}`}>
      <div className={`UpgradePlanMain ${renewalOnly ? "renewalMode" : ""}`}>
        <div className={`UpgradePlanHeader ${renewalOnly ? "renewalMode" : ""}`}>
          <h1>{renewalOnly ? "Renew Plan" : "Upgrade Plan"}</h1>
          <button
            type="button"
            className="closeButton"
            onClick={() => navigate("/homepage")}
          >
            ×
          </button>
        </div>

        <div className="BillingToggle">
          <button
            type="button"
            className={`billingTab ${billingPeriod === "monthly" ? "active" : ""
              }`}
            onClick={() => setBillingPeriod("monthly")}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`billingTab ${billingPeriod === "annual" ? "active" : ""
              }`}
            onClick={() => setBillingPeriod("annual")}
          >
            Annually
          </button>
        </div>

        {loading ? (
          <div className="upgradeLoading">Loading plans…</div>
        ) : plansToRender.length === 0 ? (
          <div className="upgradeLoading">No plans available</div>
        ) : (
          <div
            className={`UpgradePlanWrapper ${plansToRender.length > 4 ? "cols-5" : ""} ${renewalOnly ? "singleCard" : ""
              }`}
          >
            {plansToRender.map((plan, index) => {
              const isCurrent = index === 0;
              const isContactPlan = plan.isContact || isContactUsPlan(plan.name);
              const buttonTitle = renewalOnly
                ? (isContactPlan ? "Talk to us" : "Renew Plan")
                : (isCurrent
                  ? "Current Plan"
                  : isContactPlan
                    ? "Talk to us"
                    : "Upgrade");
              const isDisabled = renewalOnly
                ? upgradeInProgress === plan.planId
                : (isCurrent || upgradeInProgress === plan.planId);

              const isActive = activePlan === plan.name;

              return (
                <React.Fragment key={plan.name}>
                  {!renewalOnly && (
                    <div
                      className={`mobile-plan-header ${isActive ? "open" : ""}`}
                      onClick={() => setActivePlan(isActive ? null : plan.name)}
                    >
                      <span className="plan-name">
                        {plan.name} {plan.name === "Basic" ? "" : (isCurrent ? "(Current Plan)" : plan.badge ? `(${plan.badge})` : "")}
                      </span>
                    </div>
                  )}
                  <div className={`PricingStandard ${isActive ? "active" : ""}`}>
                    <div className="PricingStandardCard">
                      <div className="standard">
                        <h1>
                          {plan.name}
                          {plan.badge && (
                            <span className="planBadge">
                              {plan.badge}
                            </span>
                          )}
                        </h1>
                        <div className="pay">
                          <strong>
                            {plan.price === 0 ? "Free" : formatPrice(getPlanPrice(plan.price))}
                          </strong>
                          {plan.price !== 0 && (
                            <p>{billingPeriod === "monthly" ? "/ month" : "/ year"}</p>
                          )}
                        </div>
                        <div className="storage">
                          <div className="storageUser">
                            <p>Devices</p>
                            <strong>{plan.storage.users}</strong>
                          </div>
                          <div className="storageUser">
                            <p>Storage</p>
                            <strong>{plan.storage.user}</strong>
                          </div>
                          <div className="storageUser">
                            <p>Spaces</p>
                            <strong>{plan.storage.team}</strong>
                          </div>
                        </div>
                        <div className="planMeta">
                          {plan.stats?.map((stat, idx) => (
                            <div className="metaItem" key={idx}>
                              <p>{stat.label}</p>
                              <strong>{stat.value}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="text">
                        <p>What you will get</p>
                        <span></span>
                      </div>
                      <div className="email">
                        {plan.features.map((feature, idx) => (
                          <FeatureItem key={idx} icon={emailStar} text={feature} />
                        ))}
                      </div>
                      <div className="buttons">
                        <Button
                          type="violet"
                          title={buttonTitle}
                          disabled={isDisabled}
                          onClick={
                            isDisabled
                              ? undefined
                              : isContactPlan
                                ? () => window.location.href = "mailto:sales@induz.com"
                                : renewalOnly
                                  ? () => handleUpgradeClick(plan, { isRenewal: true })
                                  : () => handleUpgradeClick(plan)
                          }
                          btnType="button"
                          className="get"
                        />
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

    </section>
  );
}

export default UpgradePlan;


